import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A bespoke filter control's value, stored in a bare URL param outside
 * DataTable's `inv-mcp.`-namespaced state (the source buttons' `pr`, the
 * enforcement chips' `ev`). `parse` maps the raw value to the control's type
 * (`null` = the param is absent or invalid); setting `null` removes it.
 *
 * Writes use the functional `setSearchParams` form, so a write can never
 * replay a stale snapshot over params DataTable changed in the meantime.
 */
export function useBareUrlParam<T extends string>(
  name: string,
  parse: (raw: string | null) => T | null
): [T | null, (next: T | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = parse(searchParams.get(name));

  const setValue = useCallback(
    (next: T | null) => {
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev);
          if (next) {
            nextParams.set(name, next);
          } else {
            nextParams.delete(name);
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [name, setSearchParams]
  );

  return [value, setValue];
}
